import { Stack } from '@mui/material'

import { ExpandButton } from '@/components/Navigo/components/ExpandButton'
import { Separator } from '@/components/Separator'
import { useHasPrivileges, useIsSimulation } from '@/hooks'

import { useRoutesNavigator } from '../../../../hooks/useRoutesNavigator'
import { BuildButton } from './components/BuildButton'
import { DispatchButton } from './components/DispatchButton'
import { EditButton } from './components/EditButton'
import { ExportRoutesButton } from './components/ExportRoutesButton'
import { ExportToEmailButton } from './components/ExportToEmailButton'
import { LockButton } from './components/LockButton'
import { ReverseButton } from './components/ReverseButton'
import { RevokeButton } from './components/RevokeButton'
import { RouteDetailsButton } from './components/RouteDetailsButton'
import { SwapButton } from './components/SwapButton'
import { UnassignButton } from './components/UnassignButton'
import { UnlockButton } from './components/UnlockButton'
import { VehicleJsonButton } from './components/VehicleJsonButton'

import { useActions } from './hooks/useActions'
import { useModalActions } from './hooks/useModalActions'
import { usePermissions } from './hooks/usePermissions'
import { useRoutesOrderStepIds } from './hooks/useRoutesOrderStepIds'
import { StyledStack } from './hooks/useStyles'
import { disabledByPermission, showByPermission } from './utils'

interface Props {
  expanded: boolean
  toggleExpanded: () => void
}

export function Actions(props: Props) {
  const { expanded, toggleExpanded } = props

  const { currentRouteIds } = useRoutesNavigator()
  const orderStepIds = useRoutesOrderStepIds(currentRouteIds)
  const permissions = usePermissions()

  const { modals, modalActions } = useModalActions(orderStepIds)
  const canExportInSimulation = useHasPrivileges(['export'], true)
  const isSimulation = useIsSimulation()
  const actions = useActions(modalActions.showBuildProgressModal)

  const multiple = currentRouteIds.length > 1

  return (
    <Stack
      direction="row"
      data-trackid="navigo-routes-overview-actions"
      data-testid="navigo-routes-overview-actions"
      alignItems="center"
      width="auto"
      height="100%"
    >
      {expanded && (
        <StyledStack direction="row" height="100%" width="auto">
          {showByPermission(permissions.vehicleJson) && (
            <>
              <VehicleJsonButton currentRouteIds={currentRouteIds} />
              <Separator />
            </>
          )}

          {showByPermission(permissions.routeDetails) && (
            <>
              <RouteDetailsButton
                onClick={actions.toggleRouteDetails}
                disabled={disabledByPermission(permissions.routeDetails)}
              />
              <Separator />
            </>
          )}

          {showByPermission(permissions.lock) && (
            <LockButton
              multiple={multiple}
              onClick={actions.lock}
              disabled={disabledByPermission(permissions.lock)}
            />
          )}

          {showByPermission(permissions.unlock) && (
            <UnlockButton
              multiple={multiple}
              onClick={actions.unlock}
              disabled={disabledByPermission(permissions.unlock)}
            />
          )}

          {showByPermission(permissions.swap) && (
            <SwapButton onClick={actions.swap} disabled={disabledByPermission(permissions.swap)} />
          )}

          <ReverseButton onClick={actions.invert} permission={permissions.reverse} />

          <Separator />

          {showByPermission(permissions.exportToEmail) && (
            <ExportToEmailButton
              onClick={actions.exportToDriver}
              disabled={disabledByPermission(permissions.exportToEmail)}
            />
          )}

          {showByPermission(permissions.dispatch) && (
            <DispatchButton onClick={actions.dispatch} permission={permissions.dispatch} />
          )}

          {showByPermission(permissions.revoke) && (
            <RevokeButton
              multiple={multiple}
              onClick={actions.revoke}
              permission={permissions.revoke}
            />
          )}

          {(showByPermission(permissions.exportToEmail) ||
            showByPermission(permissions.dispatch) ||
            showByPermission(permissions.revoke)) && <Separator />}

          <ExportRoutesButton
            multiple={multiple}
            isSimulation={isSimulation}
            canExportInSimulation={canExportInSimulation}
            onShowExportRoutesModal={modalActions.showExportRoutesModal}
            onExportManifest={actions.onExportManifest}
            onExportDispatchedManifest={actions.onExportDispatchedManifest}
            canExportDispatchedManifest={actions.canExportDispatchedManifest()}
            disabled={disabledByPermission(permissions.export)}
          />

          {showByPermission(permissions.unassign) && (
            <UnassignButton
              multiple={multiple}
              onClick={modalActions.showUnassignOrderModal}
              permission={permissions.unassign}
            />
          )}

          {showByPermission(permissions.build) && (
            <BuildButton onClick={actions.build} permission={permissions.build} />
          )}

          <Separator />

          <EditButton onClick={actions.edit} disabled={disabledByPermission(permissions.edit)} />
        </StyledStack>
      )}

      <ExpandButton expanded={expanded} toggleExpanded={toggleExpanded} />

      {modals}
    </Stack>
  )
}
